import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Container, Row, Col, Card, Badge, Spinner, Button } from 'react-bootstrap';
import api, { getMediaUrl } from '../services/api';
import moment from 'moment';

const ProjectDetail = () => {
    const { slug } = useParams();
    const [project, setProject] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchProject = async () => {
            try {
                const response = await api.get(`/projects/${slug}/`);
                setProject(response.data);
            } catch (error) {
                console.error('Error fetching project detail:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchProject();
    }, [slug]);

    const getStatusColor = (status) => {
        switch (status) {
            case 'ongoing': return 'warning';
            case 'completed': return 'success';
            case 'planned': return 'primary';
            case 'on_hold': return 'danger';
            default: return 'secondary';
        }
    };

    if (loading) {
        return (
            <Container className="py-5 text-center">
                <Spinner animation="border" variant="primary" />
            </Container>
        );
    }

    if (!project) {
        return (
            <Container className="py-5 text-center">
                <h3>Project not found</h3>
                <Link to="/projects">
                    <Button variant="primary" className="mt-3">Back to Projects</Button>
                </Link>
            </Container>
        );
    }

    return (
        <Container className="py-5">
            <nav aria-label="breadcrumb" className="mb-4">
                <ol className="breadcrumb">
                    <li className="breadcrumb-item"><Link to="/">Home</Link></li>
                    <li className="breadcrumb-item"><Link to="/projects">Projects</Link></li>
                    <li className="breadcrumb-item active">{project.title}</li>
                </ol>
            </nav>

            <Row>
                <Col lg={8}>
                    <div className="mb-4">
                        <Badge bg={getStatusColor(project.status)} className="mb-2 px-3 py-2">
                            {project.status.toUpperCase()}
                        </Badge>
                        <h1 className="display-5 fw-bold mb-3">{project.title}</h1>
                        <p className="lead text-muted">{project.summary}</p>
                    </div>

                    {project.image && (
                        <div className="project-detail-image mb-4 rounded overflow-hidden shadow">
                            <img
                                src={getMediaUrl(project.image)}
                                alt={project.title}
                                className="img-fluid w-100"
                                style={{ maxHeight: '500px', objectFit: 'cover' }}
                            />
                        </div>
                    )}

                    <div className="project-content rich-text mb-5">
                        <div dangerouslySetInnerHTML={{ __html: project.content }} />
                    </div>
                </Col>

                <Col lg={4}>
                    <Card className="border-0 shadow-sm sticky-top-offset">
                        <Card.Header className="bg-primary text-white py-3 fw-bold">
                            Project Information
                        </Card.Header>
                        <Card.Body>
                            <ul className="list-unstyled mb-0">
                                {project.location && (
                                    <li className="mb-3 d-flex align-items-start">
                                        <i className="fas fa-map-marker-alt text-primary mt-1 me-3"></i>
                                        <div>
                                            <div className="fw-bold small text-muted">LOCATION</div>
                                            <div>{project.location}</div>
                                        </div>
                                    </li>
                                )}
                                {project.estimated_cost && (
                                    <li className="mb-3 d-flex align-items-start">
                                        <i className="fas fa-hand-holding-usd text-primary mt-1 me-3"></i>
                                        <div>
                                            <div className="fw-bold small text-muted">ESTIMATED COST</div>
                                            <div>{project.estimated_cost}</div>
                                        </div>
                                    </li>
                                )}
                                {project.start_date && (
                                    <li className="mb-3 d-flex align-items-start">
                                        <i className="fas fa-calendar-alt text-primary mt-1 me-3"></i>
                                        <div>
                                            <div className="fw-bold small text-muted">START DATE</div>
                                            <div>{moment(project.start_date).format('MMMM DD, YYYY')}</div>
                                        </div>
                                    </li>
                                )}
                                {project.completion_date && (
                                    <li className="mb-3 d-flex align-items-start">
                                        <i className="fas fa-calendar-check text-primary mt-1 me-3"></i>
                                        <div>
                                            <div className="fw-bold small text-muted">TARGET COMPLETION</div>
                                            <div>{moment(project.completion_date).format('MMMM DD, YYYY')}</div>
                                        </div>
                                    </li>
                                )}
                            </ul>
                            <div className="mt-4 pt-3 border-top">
                                <Button variant="outline-secondary" className="w-100" onClick={() => window.print()}>
                                    <i className="fas fa-print me-2"></i> Print Details
                                </Button>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
};

export default ProjectDetail;
